import React, { useState, useEffect } from 'react';
import { payrollAPI, staffAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { todayLocal, parseCalendarDay } from '../utils/localDate';
import { formatApiError } from '../utils/errorMessage';

const Payroll = ({ selectedBusiness }) => {
  const [payroll, setPayroll] = useState([]);
  const [staff, setStaff] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: ''
  });

  const [formData, setFormData] = useState({
    staff_id: '',
    business_id: '',
    pay_period_start: '',
    pay_period_end: '',
    pay_date: todayLocal(),
    check_number: '',
    regular_hours: '0',
    overtime_hours: '0',
    holiday_hours: '0',
    sick_hours: '0',
    pto_hours: '0',
    regular_pay: '0',
    overtime_pay: '0',
    holiday_pay: '0',
    bonus: '0',
    commission: '0',
    other_pay: '0',
    federal_tax: '0',
    state_tax: '0',
    social_security: '0',
    medicare: '0',
    health_insurance_deduction: '0',
    dental_insurance_deduction: '0',
    vision_insurance_deduction: '0',
    retirement_401k_deduction: '0',
    other_deductions: '0',
    employer_401k_match: '0',
    employer_health_insurance: '0',
    employer_taxes: '0',
    payment_method: 'direct_deposit',
    payment_status: 'pending',
    transaction_id: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedBusiness, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        ...(selectedBusiness && { business_id: selectedBusiness }),
        ...filters
      };
      
      const [payrollRes, statsRes, staffRes] = await Promise.all([
        payrollAPI.getAll(params),
        payrollAPI.getSummary(params),
        staffAPI.getAll(selectedBusiness ? { business_id: selectedBusiness, employment_status: 'active' } : { employment_status: 'active' })
      ]);
      
      setPayroll(payrollRes.data);
      setStats(statsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error('Failed to load payroll data:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      staff_id: '',
      business_id: '',
      pay_period_start: '',
      pay_period_end: '',
      pay_date: todayLocal(),
      check_number: '',
      regular_hours: '0',
      overtime_hours: '0',
      holiday_hours: '0',
      sick_hours: '0',
      pto_hours: '0',
      regular_pay: '0',
      overtime_pay: '0',
      holiday_pay: '0',
      bonus: '0',
      commission: '0',
      other_pay: '0',
      federal_tax: '0',
      state_tax: '0',
      social_security: '0',
      medicare: '0',
      health_insurance_deduction: '0',
      dental_insurance_deduction: '0',
      vision_insurance_deduction: '0',
      retirement_401k_deduction: '0',
      other_deductions: '0',
      employer_401k_match: '0',
      employer_health_insurance: '0',
      employer_taxes: '0',
      payment_method: 'direct_deposit',
      payment_status: 'pending',
      transaction_id: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPayroll) {
        await payrollAPI.update(editingPayroll.payroll_id, formData);
      } else {
        await payrollAPI.create({
          ...formData,
          business_id: selectedBusiness || formData.business_id
        });
      }
      setShowModal(false);
      setEditingPayroll(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save payroll:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to save payroll record');
    }
  };

  const openPaystub = async (id) => {
    try {
      const response = await payrollAPI.getPaystubPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      alert(formatApiError(error, 'Failed to open paystub'));
    }
  };

  const handleEdit = (payrollRecord) => {
    setEditingPayroll(payrollRecord);
    setFormData({
      staff_id: payrollRecord.staff_id || '',
      business_id: payrollRecord.business_id || '',
      pay_period_start: payrollRecord.pay_period_start ? payrollRecord.pay_period_start.split('T')[0] : '',
      pay_period_end: payrollRecord.pay_period_end ? payrollRecord.pay_period_end.split('T')[0] : '',
      pay_date: payrollRecord.pay_date ? payrollRecord.pay_date.split('T')[0] : '',
      check_number: payrollRecord.check_number || '',
      regular_hours: payrollRecord.regular_hours || '0',
      overtime_hours: payrollRecord.overtime_hours || '0',
      holiday_hours: payrollRecord.holiday_hours || '0',
      sick_hours: payrollRecord.sick_hours || '0',
      pto_hours: payrollRecord.pto_hours || '0',
      regular_pay: payrollRecord.regular_pay || '0',
      overtime_pay: payrollRecord.overtime_pay || '0',
      holiday_pay: payrollRecord.holiday_pay || '0',
      bonus: payrollRecord.bonus || '0',
      commission: payrollRecord.commission || '0',
      other_pay: payrollRecord.other_pay || '0',
      federal_tax: payrollRecord.federal_tax || '0',
      state_tax: payrollRecord.state_tax || '0',
      social_security: payrollRecord.social_security || '0',
      medicare: payrollRecord.medicare || '0',
      health_insurance_deduction: payrollRecord.health_insurance_deduction || '0',
      dental_insurance_deduction: payrollRecord.dental_insurance_deduction || '0',
      vision_insurance_deduction: payrollRecord.vision_insurance_deduction || '0',
      retirement_401k_deduction: payrollRecord.retirement_401k_deduction || '0',
      other_deductions: payrollRecord.other_deductions || '0',
      employer_401k_match: payrollRecord.employer_401k_match || '0',
      employer_health_insurance: payrollRecord.employer_health_insurance || '0',
      employer_taxes: payrollRecord.employer_taxes || '0',
      payment_method: payrollRecord.payment_method || 'direct_deposit',
      payment_status: payrollRecord.payment_status || 'pending',
      transaction_id: payrollRecord.transaction_id || '',
      notes: payrollRecord.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) return;
    
    try {
      await payrollAPI.delete(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete payroll:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete payroll record');
    }
  };

  // Calculate totals dynamically
  const calculateGrossPay = () => {
    return (
      parseFloat(formData.regular_pay || 0) +
      parseFloat(formData.overtime_pay || 0) +
      parseFloat(formData.holiday_pay || 0) +
      parseFloat(formData.bonus || 0) +
      parseFloat(formData.commission || 0) +
      parseFloat(formData.other_pay || 0)
    ).toFixed(2);
  };

  const calculateTotalDeductions = () => {
    return (
      parseFloat(formData.federal_tax || 0) +
      parseFloat(formData.state_tax || 0) +
      parseFloat(formData.social_security || 0) +
      parseFloat(formData.medicare || 0) +
      parseFloat(formData.health_insurance_deduction || 0) +
      parseFloat(formData.dental_insurance_deduction || 0) +
      parseFloat(formData.vision_insurance_deduction || 0) +
      parseFloat(formData.retirement_401k_deduction || 0) +
      parseFloat(formData.other_deductions || 0)
    ).toFixed(2);
  };

  const calculateNetPay = () => {
    return (parseFloat(calculateGrossPay()) - parseFloat(calculateTotalDeductions())).toFixed(2);
  };

  if (loading) {
    return <div className="p-6">Loading payroll data...</div>;
  }

  return (
    <ErrorBoundary section="Payroll Management">
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Payroll Management</h1>
            <p className="text-gray-600 mt-1">Track and manage employee compensation</p>
          </div>
          <button
            onClick={() => {
              setEditingPayroll(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Add Payroll Record
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Payroll Records</div>
              <div className="text-2xl font-bold text-gray-800">{stats.total_payroll_records || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Gross Pay</div>
              <div className="text-2xl font-bold text-green-600">
                ${parseFloat(stats.total_gross_pay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Deductions</div>
              <div className="text-2xl font-bold text-red-600">
                ${parseFloat(stats.total_deductions || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Net Pay</div>
              <div className="text-2xl font-bold text-blue-600">
                ${parseFloat(stats.total_net_pay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              {[2025, 2024, 2023].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Months</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                <option key={month} value={month}>
                  {new Date(2025, month-1, 1).toLocaleDateString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Payroll Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Pay</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deductions</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Pay</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payroll.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  No payroll records found for the selected period. Add your first payroll record to get started.
                </td>
              </tr>
            ) : (
              payroll.map((record) => (
                <tr key={record.payroll_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {record.first_name} {record.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{record.job_title}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {record.pay_period_start && parseCalendarDay(record.pay_period_start).toLocaleDateString()} - {record.pay_period_end && parseCalendarDay(record.pay_period_end).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {record.pay_date && parseCalendarDay(record.pay_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">
                    ${parseFloat(record.gross_pay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-red-600">
                    ${parseFloat(record.total_deductions || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-medium text-green-600">
                    ${parseFloat(record.net_pay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      record.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      record.payment_status === 'processed' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {record.payment_status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button onClick={() => openPaystub(record.payroll_id)}
                      className="text-gray-700 hover:text-gray-900 mr-3">Paystub</button>
                    <button onClick={() => handleEdit(record)}
                      className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                    <button onClick={() => handleDelete(record.payroll_id)}
                      className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[95vh] overflow-y-auto my-8">
            <h2 className="text-2xl font-bold mb-4">
              {editingPayroll ? 'Edit Payroll Record' : 'Add Payroll Record'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                  <select required value={formData.staff_id}
                    onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="">Select Employee</option>
                    {staff.map(s => (
                      <option key={s.staff_id} value={s.staff_id}>{s.first_name} {s.last_name} - {s.job_title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check Number</label>
                  <input type="text" value={formData.check_number}
                    onChange={(e) => setFormData({ ...formData, check_number: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period Start *</label>
                  <input type="date" required value={formData.pay_period_start}
                    onChange={(e) => setFormData({ ...formData, pay_period_start: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period End *</label>
                  <input type="date" required value={formData.pay_period_end}
                    onChange={(e) => setFormData({ ...formData, pay_period_end: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Date *</label>
                  <input type="date" required value={formData.pay_date}
                    onChange={(e) => setFormData({ ...formData, pay_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              {/* Hours */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Hours Worked</h3>
              <div className="grid grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regular Hours</label>
                  <input type="number" step="0.01" value={formData.regular_hours}
                    onChange={(e) => setFormData({ ...formData, regular_hours: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Hours</label>
                  <input type="number" step="0.01" value={formData.overtime_hours}
                    onChange={(e) => setFormData({ ...formData, overtime_hours: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Hours</label>
                  <input type="number" step="0.01" value={formData.holiday_hours}
                    onChange={(e) => setFormData({ ...formData, holiday_hours: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sick Hours</label>
                  <input type="number" step="0.01" value={formData.sick_hours}
                    onChange={(e) => setFormData({ ...formData, sick_hours: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PTO Hours</label>
                  <input type="number" step="0.01" value={formData.pto_hours}
                    onChange={(e) => setFormData({ ...formData, pto_hours: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              {/* Earnings */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Earnings</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regular Pay</label>
                  <input type="number" step="0.01" value={formData.regular_pay}
                    onChange={(e) => setFormData({ ...formData, regular_pay: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Pay</label>
                  <input type="number" step="0.01" value={formData.overtime_pay}
                    onChange={(e) => setFormData({ ...formData, overtime_pay: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Pay</label>
                  <input type="number" step="0.01" value={formData.holiday_pay}
                    onChange={(e) => setFormData({ ...formData, holiday_pay: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bonus</label>
                  <input type="number" step="0.01" value={formData.bonus}
                    onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commission</label>
                  <input type="number" step="0.01" value={formData.commission}
                    onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Pay</label>
                  <input type="number" step="0.01" value={formData.other_pay}
                    onChange={(e) => setFormData({ ...formData, other_pay: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <span className="font-semibold">Gross Pay: ${calculateGrossPay()}</span>
              </div>

              {/* Deductions */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Tax Deductions</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Federal Tax</label>
                  <input type="number" step="0.01" value={formData.federal_tax}
                    onChange={(e) => setFormData({ ...formData, federal_tax: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State Tax</label>
                  <input type="number" step="0.01" value={formData.state_tax}
                    onChange={(e) => setFormData({ ...formData, state_tax: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Social Security</label>
                  <input type="number" step="0.01" value={formData.social_security}
                    onChange={(e) => setFormData({ ...formData, social_security: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medicare</label>
                  <input type="number" step="0.01" value={formData.medicare}
                    onChange={(e) => setFormData({ ...formData, medicare: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Benefit Deductions</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Health Insurance</label>
                  <input type="number" step="0.01" value={formData.health_insurance_deduction}
                    onChange={(e) => setFormData({ ...formData, health_insurance_deduction: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dental Insurance</label>
                  <input type="number" step="0.01" value={formData.dental_insurance_deduction}
                    onChange={(e) => setFormData({ ...formData, dental_insurance_deduction: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vision Insurance</label>
                  <input type="number" step="0.01" value={formData.vision_insurance_deduction}
                    onChange={(e) => setFormData({ ...formData, vision_insurance_deduction: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">401k Deduction</label>
                  <input type="number" step="0.01" value={formData.retirement_401k_deduction}
                    onChange={(e) => setFormData({ ...formData, retirement_401k_deduction: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Deductions</label>
                  <input type="number" step="0.01" value={formData.other_deductions}
                    onChange={(e) => setFormData({ ...formData, other_deductions: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>
              <div className="bg-red-50 p-3 rounded">
                <span className="font-semibold">Total Deductions: ${calculateTotalDeductions()}</span>
              </div>

              {/* Employer Contributions */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Employer Contributions</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employer 401k Match</label>
                  <input type="number" step="0.01" value={formData.employer_401k_match}
                    onChange={(e) => setFormData({ ...formData, employer_401k_match: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employer Health Insurance</label>
                  <input type="number" step="0.01" value={formData.employer_health_insurance}
                    onChange={(e) => setFormData({ ...formData, employer_health_insurance: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employer Taxes</label>
                  <input type="number" step="0.01" value={formData.employer_taxes}
                    onChange={(e) => setFormData({ ...formData, employer_taxes: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2" />
                </div>
              </div>

              {/* Payment Details */}
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mt-4">Payment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="direct_deposit">Direct Deposit</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                  <select value={formData.payment_status}
                    onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2">
                    <option value="pending">Pending</option>
                    <option value="processed">Processed</option>
                    <option value="paid">Paid</option>
                    <option value="void">Void</option>
                  </select>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded">
                <span className="font-semibold text-xl">Net Pay: ${calculateNetPay()}</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="2" className="w-full border border-gray-300 rounded px-3 py-2"></textarea>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button"
                  onClick={() => { setShowModal(false); setEditingPayroll(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingPayroll ? 'Update' : 'Create'} Payroll Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default Payroll;
